import {
  extensionFromKey,
  isVideoKey,
  MEDIA_UPLOAD_SCOPE_DOMAINS,
  type MediaUploadScopeDomain,
  mediaPosterUrlFromKey,
  mediaUrlFromKey,
} from "./paths";

export type MediaKind = "image" | "video";
export type MediaProvider = "r2" | "klipy";

export type { MediaUploadScopeDomain };
export { MEDIA_UPLOAD_SCOPE_DOMAINS };

export type MediaTag = { userId: string; x: number; y: number };
export const MAX_TAGS_PER_IMAGE = 20;

export type MediaPosterAsset = { key: string; url: string };

export type MediaHlsAsset = { key: string; url: string };

/** Persisted shape for media uploaded to Hiive's R2 bucket. */
export type MediaAssetSlim = {
  id: string;
  key: string;
  order: number;
  posterKey?: string;
  /** HLS master playlist key (`<asset>.m3u8`), written by the video pipeline. */
  hlsKey?: string;
  variants?: Array<{ key: string; width: number; height: number }>;
  width?: number;
  height?: number;
  /** Base64 ThumbHash placeholder captured at upload. */
  blurHash?: string;
  tags?: MediaTag[];
};

/** Persisted shape for a GIF or sticker hosted by KLIPY. */
export type ExternalMediaAsset = {
  id: string;
  key: string;
  order: number;
  provider: "klipy";
  url: string;
  kind: MediaKind;
  mimeType?: string;
  title?: string | null;
  width?: number;
  height?: number;
};

export type MediaAsset = MediaAssetSlim | ExternalMediaAsset;

export type RenderableMediaAsset = MediaAssetSlim & {
  url: string;
  kind: MediaKind;
  mimeType: string;
  provider: MediaProvider;
  poster?: MediaPosterAsset;
  hls?: MediaHlsAsset;
  title?: string | null;
};

export type NormalizedMediaAsset = RenderableMediaAsset;

const mimeFromExtension = (extension?: string): string => {
  switch (extension) {
    case "avif":
      return "image/avif";
    case "webp":
      return "image/webp";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "heic":
      return "image/heic";
    case "heif":
      return "image/heif";
    case "bmp":
      return "image/bmp";
    case "m3u8":
      return "application/vnd.apple.mpegurl";
    case "ts":
      return "video/mp2t";
    case "tiff":
    case "tif":
      return "image/tiff";
    case "webm":
      return "video/webm";
    case "mov":
      return "video/quicktime";
    case "m4v":
    case "mp4":
      return "video/mp4";
    case "3gpp":
    case "3gp":
      return "video/3gpp";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    default:
      return "application/octet-stream";
  }
};

export const inferKind = (key: string, explicit?: MediaKind): MediaKind =>
  explicit ?? (isVideoKey(key) ? "video" : "image");

const validDimension = (value: unknown): number | undefined => {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  const rounded = Math.round(value);
  return rounded > 0 && rounded <= 16_384 ? rounded : undefined;
};

const validBlurHash = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= 100 ? trimmed : undefined;
};

const clamp01 = (value: number): number =>
  value < 0 ? 0 : value > 1 ? 1 : value;

export const sanitizeMediaTags = (
  value: unknown,
  kind: MediaKind,
): MediaTag[] | undefined => {
  if (kind !== "image" || !Array.isArray(value)) return undefined;

  const seen = new Set<string>();
  const tags: MediaTag[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object") continue;
    const candidate = raw as Partial<MediaTag>;
    if (!candidate.userId || typeof candidate.userId !== "string") continue;
    if (
      typeof candidate.x !== "number" ||
      !Number.isFinite(candidate.x) ||
      typeof candidate.y !== "number" ||
      !Number.isFinite(candidate.y) ||
      seen.has(candidate.userId)
    ) {
      continue;
    }
    seen.add(candidate.userId);
    tags.push({
      userId: candidate.userId,
      x: clamp01(candidate.x),
      y: clamp01(candidate.y),
    });
    if (tags.length >= MAX_TAGS_PER_IMAGE) break;
  }
  return tags.length > 0 ? tags : undefined;
};

export const isMediaAssetSlim = (value: unknown): value is MediaAssetSlim => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<MediaAssetSlim>;
  return (
    typeof candidate.id === "string" &&
    candidate.id.length > 0 &&
    typeof candidate.key === "string" &&
    candidate.key.length > 0 &&
    typeof candidate.order === "number" &&
    Number.isInteger(candidate.order) &&
    candidate.order >= 0
  );
};

export const isExternalMediaAsset = (
  value: unknown,
): value is ExternalMediaAsset => {
  if (!isMediaAssetSlim(value)) return false;
  const candidate = value as Partial<ExternalMediaAsset>;
  return (
    candidate.provider === "klipy" &&
    typeof candidate.url === "string" &&
    candidate.url.length > 0 &&
    (candidate.kind === "image" || candidate.kind === "video")
  );
};

export const normalizeMediaAsset = (
  value: MediaAsset,
): NormalizedMediaAsset | null => {
  if (isExternalMediaAsset(value)) {
    return {
      id: value.id,
      key: value.key,
      order: value.order,
      width: validDimension(value.width),
      height: validDimension(value.height),
      url: value.url,
      kind: value.kind,
      mimeType:
        value.mimeType ?? mimeFromExtension(extensionFromKey(value.key)),
      provider: "klipy",
      title: value.title,
    };
  }

  if (!isMediaAssetSlim(value)) return null;
  const kind = inferKind(value.key);
  const posterKey =
    typeof value.posterKey === "string" && value.posterKey.length > 0
      ? value.posterKey
      : undefined;
  const hlsKey =
    typeof value.hlsKey === "string" && value.hlsKey.length > 0
      ? value.hlsKey
      : undefined;

  return {
    id: value.id,
    key: value.key,
    order: value.order,
    posterKey,
    hlsKey,
    variants: value.variants,
    width: validDimension(value.width),
    height: validDimension(value.height),
    blurHash: validBlurHash(value.blurHash),
    tags: sanitizeMediaTags(value.tags, kind),
    url: mediaUrlFromKey(hlsKey ?? value.key),
    kind,
    mimeType: mimeFromExtension(extensionFromKey(hlsKey ?? value.key)),
    provider: "r2",
    poster: posterKey
      ? { key: posterKey, url: mediaPosterUrlFromKey(posterKey) }
      : undefined,
    hls: hlsKey ? { key: hlsKey, url: mediaUrlFromKey(hlsKey) } : undefined,
  };
};

export const normalizeMediaArray = (value: unknown): NormalizedMediaAsset[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => normalizeMediaAsset(item as MediaAsset))
    .filter((item): item is NormalizedMediaAsset => item !== null)
    .sort((a, b) => a.order - b.order)
    .map((item, order) => ({ ...item, order }));
};

export const toMediaAssetSlimForWrite = (
  value: MediaAsset,
  order = value.order,
): MediaAssetSlim | null => {
  if (!isMediaAssetSlim(value) || isExternalMediaAsset(value)) return null;
  const kind = inferKind(value.key);
  return {
    id: value.id,
    key: value.key,
    order,
    posterKey: value.posterKey,
    hlsKey: value.hlsKey,
    variants: value.variants,
    width: validDimension(value.width),
    height: validDimension(value.height),
    blurHash: validBlurHash(value.blurHash),
    tags: sanitizeMediaTags(value.tags, kind),
  };
};

export const toMediaAssetSlimArrayForWrite = (
  value: unknown,
): MediaAssetSlim[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, order) => toMediaAssetSlimForWrite(item as MediaAsset, order))
    .filter((item): item is MediaAssetSlim => item !== null);
};

export const createMediaAsset = (input: MediaAssetSlim): MediaAssetSlim => ({
  ...input,
});
